import type {
  AccountAllocationGridAccount,
  AccountAllocationGridHolding,
  AccountAllocationGridReport,
} from '@components/AccountAllocationGrid/types';
import type { EditAccountAllocationsPortfolioReport } from './editAccountAllocations.queries';

type ReportHolding = EditAccountAllocationsPortfolioReport['holdings'][number];
type ReportAccount =
  EditAccountAllocationsPortfolioReport['subAccounts'][number];

const nullToUndefined = <T>(value: T | null | undefined) => value ?? undefined;

const toHolding = (holding: ReportHolding): AccountAllocationGridHolding => ({
  expectedPercentage: holding.expectedPercentage,
  isCashEquivalent: nullToUndefined(holding.isCashEquivalent),
  customExpectedPercentage: nullToUndefined(holding.customExpectedPercentage),
  financialProduct: {
    id: holding.financialProduct.id,
    ticker: nullToUndefined(holding.financialProduct.ticker),
    isCash: nullToUndefined(holding.financialProduct.isCash),
    translatedName: {
      en: nullToUndefined(holding.financialProduct.translatedName.en),
      fr: nullToUndefined(holding.financialProduct.translatedName.fr),
      es: nullToUndefined(holding.financialProduct.translatedName.es),
    },
  },
});

const toHoldings = (
  holdings: ReportHolding[],
): AccountAllocationGridHolding[] =>
  holdings
    .filter((holding) => Boolean(holding.financialProduct.id))
    .map(toHolding);

const toAccount = (account: ReportAccount): AccountAllocationGridAccount => ({
  id: account.id,
  marketValueCents: account.marketValueCents,
  subAccount: {
    id: account.subAccount.id,
    state: account.subAccount.state,
    account: {
      id: account.subAccount.account.id,
      custodianAccountNumber: nullToUndefined(
        account.subAccount.account.custodianAccountNumber,
      ),
      user: {
        firstName: nullToUndefined(account.subAccount.account.user.firstName),
        entityName: nullToUndefined(account.subAccount.account.user.entityName),
      },
    },
  },
  holdings: toHoldings(account.holdings),
});

export const toAccountAllocationGridReport = (
  report: EditAccountAllocationsPortfolioReport | null | undefined,
): AccountAllocationGridReport | null => {
  if (!report) return null;
  if (report.subAccounts.length === 0) return null;

  return {
    id: report.id,
    expectedCashPercentage: report.expectedCashPercentage,
    holdings: toHoldings(report.holdings),
    subAccounts: report.subAccounts.map(toAccount),
  };
};
